/**
 * A barreira: o que aparece no lugar da tela quando ela quebra no meio do
 * desenho.
 *
 * Sem ela um erro dentro de qualquer uma das setenta telas leva a árvore
 * inteira junto -- o React desmonta tudo até a raiz e a janela fica em branco,
 * sem nada escrito e sem nada para tocar. Neste app isso custa mais do que a
 * tela perdida: o `<audio>` mora na raiz, então a música pararia junto, e a
 * única saída seria recarregar a página, que na TV da sala é um caminho longo.
 *
 * Fica em `App.tsx` por volta do `<Suspense>`, e assim apanha duas quedas em
 * vez de uma: a tela que quebra no meio do desenho e o pedaço que nunca chega,
 * porque um `import()` recusado é atirado como erro na volta seguinte. O
 * tocador, a tarja de sessão e o idioma são irmãos de fora: uma tela que cai
 * não leva a música, nem o convite de cinema, nem a sessão.
 *
 * Classe, e não função, porque o `getDerivedStateFromError` não tem gancho
 * equivalente -- é a única peça do app que o React ainda só oferece assim.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { traduzir } from '@nosso-sempre/shared';
import { Icone } from './Icone';
import './barreira.css';

export function Barreira({ children }: { children: ReactNode }) {
  /* A rota de agora é a chave: sair desta tela limpa o erro sozinho. Por
     propriedade, e não `key={rota}` na barreira -- o `key` remontaria o
     `<Suspense>` de dentro a cada navegação, e aí toda troca de tela piscaria
     o "Abrindo..." mesmo com o pedaço já na mão. */
  const { pathname } = useLocation();
  return <Guarda chave={pathname}>{children}</Guarda>;
}

interface Propriedades {
  /** Ver `getDerivedStateFromProps`: trocar de valor devolve a tela. */
  chave: string;
  children: ReactNode;
}

interface Estado {
  erro: boolean;
  /** A chave de quando este estado nasceu, para saber quando ela mudou. */
  chave: string;
}

class Guarda extends Component<Propriedades, Estado> {
  state: Estado = { erro: false, chave: this.props.chave };

  static getDerivedStateFromError(): Partial<Estado> {
    return { erro: true };
  }

  static getDerivedStateFromProps(props: Propriedades, estado: Estado): Estado | null {
    /* A ordem é o que faz isto funcionar. Depois de um erro o React guarda o
       estado e desenha de novo, e neste segundo desenho a chave ainda é a
       mesma -- nada se limpa, o recado fica de pé. Só uma navegação de
       verdade muda a chave, e é aí que a tela ganha outra chance. */
    if (props.chave === estado.chave) return null;
    return { erro: false, chave: props.chave };
  }

  componentDidCatch(erro: Error, detalhe: ErrorInfo) {
    /* Não há serviço de erro nesta casa: o console é a única pista que sobra
       de uma queda que só acontece na TV da sala. O `componentStack` diz qual
       tela caiu, que a mensagem sozinha não diz. */
    console.error('[barreira]', erro, detalhe.componentStack);
  }

  render() {
    if (!this.state.erro) return this.props.children;
    return <Desculpa aoTentar={() => this.setState({ erro: false })} />;
  }
}

/**
 * O recado, à parte porque precisa de `useNavigate` -- e uma classe não tem
 * ganchos.
 *
 * Dois caminhos de saída, e os dois são de verdade: a moldura com a navegação
 * mora dentro das rotas, então enquanto isto está em cena não há barra de
 * baixo nem coluna da esquerda para levar a pessoa a lugar nenhum.
 *
 *  - Tentar de novo desenha a mesma tela outra vez. Vale a pena porque quase
 *    tudo que se desenha aqui vem do acervo lá fora, e o dado que faltou na
 *    primeira volta costuma vir na segunda.
 *  - Voltar ao início troca a rota, e trocar a rota é o que limpa a barreira
 *    pela chave, sem ninguém ter de a limpar à mão.
 */
function Desculpa({ aoTentar }: { aoTentar: () => void }) {
  const navegar = useNavigate();

  return (
    <main className="bar-falha">
      {/* No `caixa` e não no título: assim o leitor de tela lê o que houve e o
          que fazer de uma vez, em vez de anunciar "Alguma coisa deu errado" e
          calar-se. A tela mudou sem ninguém pedir, então tem de se anunciar. */}
      <div className="bar-falha-caixa" role="alert">
        <span className="bar-falha-icone">
          <Icone nome="erro" tamanho={36} />
        </span>
        <p className="bar-falha-titulo">{traduzir('Alguma coisa deu errado')}</p>
        <p className="bar-falha-recado">
          {traduzir('Esta tela parou. O resto da casa continua de pé.')}
        </p>
        <div className="bar-falha-botoes">
          <button className="botao" type="button" onClick={aoTentar}>
            <Icone nome="recarregar" tamanho={20} />
            {traduzir('Tentar de novo')}
          </button>
          <button className="botao calmo" type="button" onClick={() => navegar('/')}>
            <Icone nome="casa" tamanho={20} />
            {traduzir('Voltar ao início')}
          </button>
        </div>
      </div>
    </main>
  );
}
